import React, {
  Dispatch,
  memo,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { Sport as SportType } from 'interfaces/sportsbook-data-levels';
import { FavoriteCompetition } from './__partials/FavoriteCompetition';
import { FavoriteEvents } from './__partials/FavoriteEvents';
import { FavoriteSkeletonHeader } from './FavoriteSkeletonHeader';
import { Competition } from 'components/SportsbookListViews/Competition';
import { CompetitionSkeleton } from 'components/UI/Skeletons/CompetitionSkeleton';
import { FetchFavoriteData } from 'newcomponents/Shared/sportsbook/Favorite/FetchFavoriteData';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  getFavoriteCompetitions,
  getFavoriteDataLoading,
  getFavoriteMatches,
  getFavoriteMatchIds
} from 'store/selectors/fav-data';
import {
  getIsLoggedIn,
  getPersonalisedDataLoaded,
  getUser
} from 'store/selectors/user-data';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type Props = {
  setLoading?: Dispatch<SetStateAction<boolean>>;
};

const COMPETITION_SKELETON_COUNTS = {
  mobile: 5,
  desktop: 1
};

const skeletonsCount =
  COMPETITION_SKELETON_COUNTS[isMobile() ? 'mobile' : 'desktop'];

export const Favorite = memo(({ setLoading }: Props): ReactElement => {
  const [collapsedCount, setCollapsedCount] = useState(1);

  const user = useSelector(getUser);
  const isLoading = useSelector(getFavoriteDataLoading);
  const favoriteMatches = useSelector(getFavoriteMatches);
  const competitions = useSelector(getFavoriteCompetitions);
  const favoriteMatchIds = useSelector(getFavoriteMatchIds);
  const personalisedDataLoaded = useSelector(getPersonalisedDataLoaded);
  const isLoggedIn = useSelector(getIsLoggedIn);

  const competitionsCount = competitions.length;
  const liveGamesCount = favoriteMatchIds.liveSports.length;
  const premGamesCount = favoriteMatchIds.prematchSports.length;

  const { params } = useSbRouteMatch();
  const configs = useSportsbookConfigs();

  useLayoutEffect(() => {
    setLoading?.(false);
  }, []);

  useEffect(() => {
    if (!liveGamesCount && !premGamesCount && competitionsCount) {
      setLoading?.(false);
    }
  }, [liveGamesCount, premGamesCount, competitionsCount]);

  const competitionElements = useCallback((sport: SportType) => {
    const competitions = [];
    const { region, alias, type } = sport;

    for (const reg of Object.values(region || {})) {
      competitions.push(
        <Competition
          key={reg.id}
          regionName={reg.name}
          regionAlias={reg.alias}
          currentSport={alias}
          sportAlias={alias}
          competitions={reg.competition}
          count={1}
          sportType={type}
          isDetailed
          favorite
        />
      );
    }

    return competitions;
  }, []);

  const regionsData = useMemo(() => {
    return ['live', 'prematch', 'competition'].map(type => {
      if (
        (liveGamesCount && type === 'live') ||
        (type === 'prematch' && premGamesCount)
      ) {
        return (
          <FavoriteEvents
            key={type}
            type={type}
            gamesCount={type === 'live' ? liveGamesCount : premGamesCount}
            favoriteMatches={
              type === 'live'
                ? favoriteMatches.liveSports
                : favoriteMatches.prematchSports
            }
            collapsedCount={collapsedCount}
            setCollapsedCount={setCollapsedCount}
            competitionElements={competitionElements}
          />
        );
      }

      if (type === 'competition' && competitionsCount) {
        return (
          <FavoriteCompetition
            key={type}
            {...{
              competitionsCount,
              collapsedCount,
              setCollapsedCount,
              competitions
            }}
          />
        );
      }
    });
  }, [
    params,
    competitions,
    liveGamesCount,
    premGamesCount,
    competitionsCount,
    favoriteMatches.liveSports,
    favoriteMatches.prematchSports
  ]);

  const personalizationBasedLoading =
    SpringConfigs.USER_PERSONALIZATION_ENABLED &&
    (user.pending || !personalisedDataLoaded);

  return (
    <div style={{ padding: 4, background: 'var(--v3-black-4)' }}>
      {SpringConfigs.USER_PERSONALIZATION_ENABLED ? (
        isLoggedIn && personalisedDataLoaded ? (
          <FetchFavoriteData
            setLoading={setLoading}
            additionalMarkets={
              configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN
            }
          />
        ) : null
      ) : (
        <FetchFavoriteData
          setLoading={setLoading}
          additionalMarkets={
            configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN
          }
        />
      )}
      {isLoading || personalizationBasedLoading ? (
        <div className="bg-black-0">
          <FavoriteSkeletonHeader />
          <CompetitionSkeleton count={skeletonsCount} />
        </div>
      ) : (
        regionsData
      )}
    </div>
  );
});
